import {
  ActionIcon,
  Box,
  Card,
  Group,
  Menu,
  Stack,
  Table,
  Text,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import type { Consumption } from '@markku/types'
import { formatDate, formatEuros } from '@markku/utils'
import { IconDotsVertical, IconEdit, IconTrash } from '@tabler/icons-react'
import { TYPE_CONFIG } from './utils'

type ConsumptionTableProps = {
  consumptions: Consumption[]
  onEdit: (consumption: Consumption) => void
  onDelete: (id: number) => void
}

export function ConsumptionTable({
  consumptions,
  onEdit,
  onDelete,
}: ConsumptionTableProps) {
  const handleDelete = (consumption: Consumption) => {
    modals.openConfirmModal({
      title: 'Poista kulutus',
      centered: true,
      children: (
        <Text>
          Haluatko varmasti poistaa tämän mittarilukeman (
          {Number(consumption.meterReading).toFixed(1)} {consumption.unit},{' '}
          {formatDate(consumption.date)})?
        </Text>
      ),
      labels: { confirm: 'Poista', cancel: 'Peruuta' },
      confirmProps: { color: 'red' },
      onConfirm: () => onDelete(consumption.id),
    })
  }

  if (consumptions.length === 0) {
    return null
  }

  return (
    <Card withBorder radius="lg" p="md">
      <Stack gap="md">
        <Text fw={600} size="sm">
          Mittarilukemat
        </Text>

        <Table.ScrollContainer minWidth={500}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Päivä</Table.Th>
                <Table.Th>Tyyppi</Table.Th>
                <Table.Th>Mittarilukema</Table.Th>
                <Table.Th>Kustannus</Table.Th>
                <Table.Th w={50}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {consumptions.map((consumption) => {
                const config = TYPE_CONFIG[consumption.type]
                const meterReading = consumption.meterReading
                  ? Number(consumption.meterReading).toFixed(1)
                  : '-'
                return (
                  <Table.Tr key={consumption.id}>
                    <Table.Td>
                      <Text size="sm">{formatDate(consumption.date)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Box
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: 4,
                            background: `var(--mantine-color-${config.color})`,
                          }}
                        />
                        <Text size="sm">{config.label}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {meterReading} {consumption.unit}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text
                        size="sm"
                        c={consumption.cost ? undefined : 'dimmed'}
                      >
                        {consumption.cost
                          ? formatEuros(parseFloat(consumption.cost))
                          : '-'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Menu position="bottom-end" withinPortal>
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray">
                            <IconDotsVertical size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            leftSection={<IconEdit size={16} />}
                            onClick={() => onEdit(consumption)}
                          >
                            Muokkaa
                          </Menu.Item>
                          <Menu.Item
                            leftSection={<IconTrash size={16} />}
                            color="red"
                            onClick={() => handleDelete(consumption)}
                          >
                            Poista
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Stack>
    </Card>
  )
}
